import React, { useState } from 'react';
import { Globe, Users, Calendar, MapPin, Building2, Coins, Twitter, ChevronDown, ChevronRight } from 'lucide-react';
import { FaLinkedin } from 'react-icons/fa';

interface DetailItem {
  label: string;
  value: string | string[] | React.ReactNode;
  icon?: React.ElementType;
}

interface DetailsCardProps {
  title: string;
  details: DetailItem[];
  className?: string;
}

const tagColors = [
  { bg: 'bg-[#E0F7FA]', text: 'text-[#00796B]' }, 
  { bg: 'bg-[#C8E6C9]', text: 'text-[#388E3C]' }, 
  { bg: 'bg-[#F8BBD0]', text: 'text-[#C2185B]' }, 
  { bg: 'bg-[#F5F5F5]', text: 'text-[#757575]' }, 
  { bg: 'bg-[#FFF9C4]', text: 'text-[#F57F17]' }, 
  { bg: 'bg-[#BBDEFB]', text: 'text-[#1976D2]' }, 
  { bg: 'bg-[#E1BEE7]', text: 'text-[#8E24AA]' },
  { bg: 'bg-[#FFCCBC]', text: 'text-[#D84315]' },
];

const getTagColor = (tag: string) => {
  const index = Math.abs(tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % tagColors.length;
  return tagColors[index];
};

const truncateUrl = (url: string, maxLength: number = 20): string => {
  if (url.length <= maxLength) return url;
  return url.substring(0, maxLength) + '...';
};

const DetailsCard: React.FC<DetailsCardProps> = ({ title, details, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Separate description from other details
  const descriptionDetail = details.find(detail => detail.label === "Description");
  const regularDetails = details.filter(detail => detail.label !== "Description");

  return (
    <div className={`flex flex-col items-start w-full p-3 ${className}`}>
      <div className="flex items-center gap-2 w-full">
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 hover:bg-[#F9FAFB] rounded-lg p-1 transition-colors duration-200"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-[#4B5563]" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[#4B5563]" />
          )}
          <h2 className="text-[16px] font-semibold text-[#222222]">{title}</h2>
        </button>
      </div>
      
      {isExpanded && (
        <div className="w-full flex flex-col min-h-0">
          <table className="w-full">
            <tbody>
              {regularDetails.map((detail, index) => {
                const Icon = detail.icon || Globe;
                return (
                  <tr 
                    key={index} 
                    className={`group transition-colors duration-200 ${
                      hoveredRow === index ? 'bg-[#F9FAFB]' : ''
                    }`}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td className="py-2 align-top w-[33%]">
                      <div className="flex items-center gap-2 text-[14px] text-[#4B5563]">
                        <Icon className="w-4 h-4 text-[#4B5563]" />
                        <span>{detail.label}</span>
                      </div>
                    </td>
                    <td className="py-2 align-top w-[67%] text-left">
                      {detail.label === "Website" ? (
                        <a 
                          href={`https://${detail.value}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#5D90D4] text-[14px] hover:underline rounded-[8px] border border-[#A7CAE9] px-2 py-[4px] truncate"
                          onClick={(e) => e.stopPropagation()}
                          title={typeof detail.value === 'string' ? detail.value : undefined}
                        >
                          www.{typeof detail.value === 'string' ? truncateUrl(detail.value) : ''}
                        </a>
                      ) : detail.label === "Categories" && Array.isArray(detail.value) ? (
                        <div className="flex items-center overflow-hidden">
                          {detail.value.slice(0, 2).map((category, idx) => {
                            const { bg, text } = getTagColor(category);
                            return (
                              <span 
                                key={idx} 
                                className={`
                                  ${bg} ${text}
                                  px-[6px] py-[2px]
                                  rounded-lg
                                  text-[14px]
                                  font-normal
                                  font-[Inter]
                                  leading-[20px]
                                  tracking-[-0.28px]
                                  whitespace-nowrap 
                                  mr-1
                                `}
                              >
                                {category.trim()}
                              </span>
                            );
                          })}
                          {detail.value.length > 2 && (
                            <span className="text-[14px] text-[#7A7A7A] ml-1 font-[Inter] font-normal leading-[20px] tracking-[-0.28px]">
                              +{detail.value.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="text-[14px] text-[#222222] text-left">
                          {detail.value}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Description Section */}
          {descriptionDetail && (
            <div className="mt-2 p-4 border border-[#EEEEEE] rounded-lg w-full max-h-[300px] overflow-hidden">
              <div className="flex items-center gap-2 ">
                {descriptionDetail.icon && <descriptionDetail.icon className="w-4 h-4 text-[#5C5E63]" />}
                <span className="text-[14px] font-medium text-[#5C5E63]">Description</span>
              </div>
              <div className="text-[14px] text-[#222222] text-left whitespace-pre-wrap leading-relaxed overflow-y-auto scrollbar-hide max-h-[220px] pr-2">
                {typeof descriptionDetail.value === 'string' ? descriptionDetail.value : 'N/A'}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailsCard; 